import { Injectable, } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { FleetChecklist, FleetChecklistSummary, FleetGroupedByType, FleetGroupedByTime, FleetChecklistCreate, FleetStatus } from '../models/fleet-checklist';
import {FleetChecklistApiService} from '../services/fleet-checklist-api.service';
import {FleetChecklistHelper} from '../helpers/fleet-checklist.helper';

@Injectable({
  providedIn: 'root'
})
export class FleetChecklistListStoreService {
  // --- Fuentes de Verdad (Privadas) ---
  private fleetsSubject = new BehaviorSubject<FleetChecklistSummary[]>([]);
  private loadingSubject = new BehaviorSubject<boolean>(true);
  private searchFilterSubject = new BehaviorSubject<string>('');
  private errorMessageSubject = new BehaviorSubject<string | null>(null);

  // --- Observables Públicos (Lo que lee el componente con el pipe | async) ---
  public fleets$ = this.fleetsSubject.asObservable();
  public loading$: Observable<boolean> = this.loadingSubject.asObservable();
  public searchFilter$ = this.searchFilterSubject.asObservable();
  public errorMessage$: Observable<string | null> = this.errorMessageSubject.asObservable();

  private debouncedSearch$: Observable<string> = this.searchFilterSubject.asObservable().pipe(
    debounceTime(300),         // Espera 300ms de silencio antes de emitir
    distinctUntilChanged()     // Solo emite si el texto realmente cambió respecto al anterior
  );

  public filteredFleets$: Observable<FleetChecklistSummary[]> = combineLatest([
    this.fleetsSubject,
    this.debouncedSearch$
  ]).pipe(
    map(([fleets, search]) => this.applyFilter(fleets, search))
  );

  public fleetsGroupedByDate$: Observable<FleetGroupedByTime[]> = this.filteredFleets$.pipe(
    map(fleets => this.groupByDateAndType(fleets))
  );

  constructor(
    private api: FleetChecklistApiService
  ) { }


  // --- Metodos del Store ---
  async fetchFleets(): Promise<void> {
    this.loadingSubject.next(true);
    this.errorMessageSubject.next(null);

    try {
      // Si tu API aún devuelve Observables, puedes usar lastValueFrom() o convertir a promesa
      const data = await this.api.getFleetsSummary();
      this.fleetsSubject.next(data || []);
    } catch (error) {
      console.error('Error fetching fleets in Store:', error);
      this.errorMessageSubject.next(
        'No se pudieron cargar las validaciones vehiculares. Intente de nuevo.'
      );
    } finally {
      this.loadingSubject.next(false);
    }
  }
  findById(id: string) : FleetChecklistSummary | null{
    return this.fleetsSubject.getValue().find(f => f.id === id) || null
  }

  setSearchTerm(term: string): void {
    this.searchFilterSubject.next(term);
  }

  addFleetChecklists(fleet: FleetChecklist[]): void {
    const newSummary: FleetChecklistSummary[] =
      fleet.map(f => FleetChecklistHelper.toSummary(f));
    const currentList = this.fleetsSubject.getValue();
    const map = new Map<string, FleetChecklistSummary>();
    // primero los actuales
    currentList.forEach(item => map.set(item.id, item));
    // luego los nuevos (sobrescriben si existe el mismo id)
    newSummary.forEach(item => map.set(item.id, item));
    this.fleetsSubject.next(Array.from(map.values()));
  }
  async duplicateFleet(id: string): Promise<void> {
    this.loadingSubject.next(true); // Opcional: activar spinner global
    try {
      let fleet = await this.findById(id);
      if(!fleet){
        this.errorMessageSubject.next('No se pudo duplicar el checklist. Intente de nuevo.');
        return;
      }
      let newFleet : FleetChecklistCreate = {
        name: `${fleet.name} - Copy`,
        status: FleetStatus.IN_PROGRESS,
        type: fleet.type,
        vehiclesIds: fleet.vehiclesIds || [],
        fields: fleet?.fields || [],
        tags: fleet?.tags || [],
        parameters: fleet?.parameters || {}
      }
      let newChecklist = await this.api.createFleetChecklist(newFleet);
      if (newChecklist) {
        let FleetSummary : FleetChecklistSummary = {
          ...newChecklist,
          vehiclesIds: []
        }
        const currentList = this.fleetsSubject.getValue();
        this.fleetsSubject.next([...currentList, FleetSummary]);
      }
    } catch (error) {
      console.error('Error global al duplicar checklist:', error);
      this.errorMessageSubject.next('No se pudo duplicar el checklist. Intente de nuevo.');
    } finally {
      this.loadingSubject.next(false);
    }
  }
  async deleteFleet(id : string) : Promise<void>{
    this.loadingSubject.next(true);
    try{
      const success = await this.api.deleteFleetChecklist(id);
      if(success){
        const currentList = this.fleetsSubject.getValue();
        this.fleetsSubject.next(currentList.filter(f => f.id !== id));
      }
    } catch(error){
      console.error('Error global al eliminar checklist:', error);
      this.errorMessageSubject.next('No se pudo eliminar el checklist. Intente de nuevo.');
    } finally {
      this.loadingSubject.next(false);
    }
  }

  // actual filter
  private applyFilter(fleets: FleetChecklistSummary[], search: string): FleetChecklistSummary[] {
    if (!fleets) return [];
    const term = search.toLowerCase().trim();
    if (!term) return fleets;

    return fleets.filter(item =>
      item.name?.toLowerCase().includes(term) ||
      item.status?.toLowerCase().includes(term) ||
      item.type?.toLowerCase().includes(term) ||
      item.tags?.some(tag => tag.toLowerCase().includes(term)) ||
      item.parameters?.['validationDate']?.toString().toLowerCase().includes(term)
    );
  }

  // group fleets

  private groupByType(items: FleetChecklistSummary[]): FleetGroupedByType[] {
    const map = new Map<string, FleetChecklistSummary[]>();

    for (const item of items) {
      const type = item.type;

      if (!map.has(type)) {
        map.set(type, []);
      }

      map.get(type)!.push(item);
    }

    return Array.from(map.entries())
      .sort(([typeA], [typeB]) => {
        const emptyA = !typeA;
        const emptyB = !typeB;

        if (emptyA && !emptyB) return 1;
        if (!emptyA && emptyB) return -1;

        return (typeA ?? '').localeCompare(typeB ?? '');
      })
      .map(([type, items]) => ({
        type,
        items: items.sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? '')
        )
      }));
  }

  private groupByDateAndType(
    fleets: FleetChecklistSummary[]
  ): FleetGroupedByTime[] {

    const map = new Map<string, FleetChecklistSummary[]>();

    // 1. Agrupar por fecha
    for (const f of fleets) {
      const key = this.getDateKey(f.parameters?.validationDate);

      if (!map.has(key)) {
        map.set(key, []);
      }

      map.get(key)!.push(f);
    }

    // 2. Convertir a estructura final
    const result: FleetGroupedByTime[] = Array.from(map.entries())
      .sort(([a], [b]) => b.localeCompare(a)) // más reciente primero
      .map(([dateKey, items]) => {

        // agrupar por tipo dentro del día
        const groups = this.groupByType(items);

        const isToday = this.isToday(dateKey) || false;
        return {
          label: isToday
            ? `Hoy, ${this.formatDateLabel(dateKey || 'Sin fecha')}`
            : this.formatDateLabel(dateKey || 'Sin fecha') || dateKey,
          isToday: this.isTodayKey(dateKey),
          groups
        };
      });

    return result;
  }

  // helpers
  private isToday(dateStr?: string): boolean {
    if (!dateStr) return false;

    const peruToday = new Date().toLocaleDateString('en-CA', {
      timeZone: 'America/Lima'
    });

    return dateStr.substring(0, 10) === peruToday;
  }

  private isTodayKey(dateKey: string): boolean {
    const todayKey = this.getDateKey(new Date().toISOString());
    return dateKey === todayKey;
  }

  private formatDateLabel(dateKey: string): string {
    if (dateKey === 'Sin fecha') return dateKey;

    const [yyyy, mm, dd] = dateKey.split('-').map(Number);
    const date = new Date(yyyy, mm - 1, dd);

    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: '2-digit',
      month: 'long'
    }).format(date);
  }
  private getDateKey(dateStr?: string): string {
    if (!dateStr || dateStr === 'Sin fecha' || dateStr.toLowerCase() === 'invalid date') {
      return 'Sin fecha';
    }

    // Validar formato YYYY-MM-DD
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateStr)) return 'Sin fecha';

    // Validar que sea una fecha real
    const date = new Date(`${dateStr}T00:00:00`);
    if (isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== dateStr) {
      return 'Sin fecha';
    }

    return dateStr;
  }
}
